import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DiningEventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(location.state?.message || '');
  const [checkInAmount, setCheckInAmount] = useState('');

  useEffect(() => {
    fetchEvent();
    // Refresh every 30 seconds to see new participants
    const interval = setInterval(fetchEvent, 30000);
    return () => clearInterval(interval);
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/dining-events/${eventId}`);
      setEvent(response.data.event);
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setActionLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/dining-events/${eventId}/join`);
      if (response.data.success) {
        setSuccess('Successfully joined the event!');
        fetchEvent();
      }
    } catch (err) {
      const reason = err.response?.data?.message || 'Unknown error';
      setError(`Unable to join the event: ${reason}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Are you sure you want to leave this event?')) return;
    
    setActionLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/dining-events/${eventId}/leave`);
      if (response.data.success) {
        setSuccess('Left the event successfully');
        fetchEvent();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to leave event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInAmount || parseFloat(checkInAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setActionLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/dining-events/${eventId}/checkin`, {
        spentAmount: parseFloat(checkInAmount)
      });
      if (response.data.success) {
        setSuccess(`Checked in! Hope you had a great time meeting your neighbors!`);
        setCheckInAmount('');
        fetchEvent();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) return;

    setActionLoading(true);
    setError('');
    try {
      const response = await axios.delete(`${API_URL}/dining-events/${eventId}`);
      if (response.data.success) {
        navigate('/dining-events', { state: { message: 'Event deleted successfully' } });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete event');
    } finally {
      setActionLoading(false);
    }
  };

  const isOrganizer = event?.organizer?._id?.toString() === user?._id?.toString();
  const isParticipant = event?.participants?.some(
    p => p.user?._id?.toString() === user?._id?.toString()
  );
  const participant = event?.participants?.find(
    p => p.user?._id?.toString() === user?._id?.toString()
  );
  const isCheckedIn = participant?.checkedIn;
  const canCheckIn = isParticipant && !isCheckedIn && new Date(event?.eventDate) <= new Date();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  };

  const formatShareDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/dining-events/${eventId}?v=${Date.now()}`;
    const title = `Join me at ${event?.restaurant?.name || 'a restaurant'} on Connect!`;
    const when = `${formatShareDate(event?.eventDate)} at ${formatTime12h(event?.eventTime)}`;
    const where = event?.restaurant?.address || '';
    let text = when;
    if (where) text += ` | ${where}`;
    if (event?.description) text += `\n${event.description}`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${title}\n${text}\n${url}`);
        setSuccess('Event link copied to clipboard!');
        setTimeout(() => setSuccess(''), 3000);
      } catch {
        setError('Failed to copy link');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Event not found</h2>
            <p className="text-gray-600 mb-6">This event may have been cancelled or doesn't exist.</p>
            <button
              onClick={() => navigate('/dining-events')}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Back to Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/dining-events')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Events
          </button>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium"
          >
            <ShareIcon className="h-4 w-4" />
            Share Event
          </button>
        </div>

        {success && (
          <div className="bg-primary-50 border border-primary-200 text-primary-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Restaurant Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">{event.restaurant?.name}</h1>
            {event.restaurant?.cuisine && (
              <p className="text-primary-100 mb-4">{event.restaurant.cuisine}</p>
            )}
            {event.restaurant?.address && (
              <div className="flex items-center text-primary-100">
                <MapPinIcon className="h-5 w-5 mr-2" />
                <span>{event.restaurant.address}</span>
              </div>
            )}
          </div>

          {/* Event Details */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-center">
                <CalendarIcon className="h-6 w-6 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDate(event.eventDate)}</p>
                </div>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-6 w-6 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="text-lg font-semibold text-gray-900">{event.eventTime}</p>
                </div>
              </div>
              <div className="flex items-center">
                <UserGroupIcon className="h-6 w-6 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Participants</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {event.participants?.length || 0} / {event.totalSeats}
                  </p>
                </div>
              </div>
              {event.restaurant?.phone && (
                <div className="flex items-center">
                  <PhoneIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-lg font-semibold text-gray-900">{event.restaurant.phone}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Event Notes</p>
                <p className="text-gray-900">{event.description}</p>
              </div>
            )}

            {/* Organizer */}
            <div className="mb-8 p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-600 mb-2">Organized by</p>
              <div className="flex items-center gap-3">
                {event.organizer?.photo ? (
                  <img src={event.organizer.photo} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-semibold">
                    {event.organizer?.name?.charAt(0) || '?'}
                  </div>
                )}
                <div>
                  <p className="text-lg font-semibold text-primary-900">{event.organizer?.name}</p>
                  {isOrganizer && (
                    <span className="inline-block px-3 py-0.5 bg-primary-600 text-white text-xs font-semibold rounded-full">
                      You are the organizer
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Participants List */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Participants ({event.participants?.length || 0})</h3>
              <div className="space-y-2">
                {event.participants?.map((participant, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      {participant.user?.photo ? (
                        <img src={participant.user.photo} alt="" className="h-10 w-10 rounded-full object-cover mr-3" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-semibold mr-3">
                          {participant.user?.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {participant.user?.name || 'Unknown'}
                          {participant.user?._id === event.organizer?._id && (
                            <span className="ml-2 text-xs text-primary-600">(Organizer)</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">
                          Joined {new Date(participant.joinedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {participant.checkedIn && (
                      <div className="flex items-center text-primary-600">
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        <span className="text-sm font-medium">Checked In</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Gender Requirements */}
            {event.requireFemale && (
              <div className="mb-8 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-sm font-medium text-primary-900">
                  👩 This event requires at least one female participant
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="border-t border-gray-200 pt-6">
              {!isParticipant && event.status === 'open' && event.availableSeats > 0 && (
                <button
                  onClick={handleJoin}
                  disabled={actionLoading}
                  className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold disabled:opacity-50"
                >
                  {actionLoading ? 'Joining...' : `Join Event (${event.availableSeats} seats left)`}
                </button>
              )}

              {isParticipant && !isOrganizer && (
                <div className="space-y-4">
                  {!isCheckedIn && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm font-medium text-yellow-900 mb-3">
                        After dining, enter your bill amount to check in!
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={checkInAmount}
                          onChange={(e) => setCheckInAmount(e.target.value)}
                          placeholder="Enter bill amount (₹)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <button
                          onClick={handleCheckIn}
                          disabled={actionLoading || !checkInAmount}
                          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                        >
                          Check In
                        </button>
                      </div>
                      <p className="text-xs text-yellow-700 mt-2">
                        💡 Thanks for being part of the Connect community!
                      </p>
                    </div>
                  )}
                  
                  {isCheckedIn && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-medium text-green-900">
                        ✅ Checked in! Thanks for joining!
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleLeave}
                    disabled={actionLoading}
                    className="w-full px-6 py-3 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-semibold disabled:opacity-50"
                  >
                    Leave Event
                  </button>
                </div>
              )}

              {isOrganizer && (
                <div className="space-y-4">
                  <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-sm text-primary-900 mb-2">
                      Thanks for organizing this Connect event!
                    </p>
                    <p className="text-xs text-blue-700">
                      You're helping build a stronger neighborhood community.
                    </p>
                  </div>

                  {event.participants?.length <= 1 && event.status !== 'cancelled' && (
                    <button
                      onClick={handleDelete}
                      disabled={actionLoading}
                      className="w-full px-6 py-3 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-semibold disabled:opacity-50"
                    >
                      Delete Event
                    </button>
                  )}
                </div>
              )}

              {event.status === 'full' && !isParticipant && (
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <p className="text-gray-600">This event is full</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiningEventDetail;
